import { Service, mapCustomServices } from "../Types/Project/Service/ServiceInterface";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProjectBasicContact } from "../Types/Project/ProjectInterface";
import { mapSubFolders, SubFolder, SubFolderAndDocument } from "../Types/Project/Service/SubFolderInterface";
import { ShareContact } from "../Types/Project/ShareContactInterface";
import { DocumentInterface, mapDocuments } from "../Types/Project/Service/DocumentInterface";
import { baseURL } from "../../Api/ApiConstant";
import { DefaultPackage, mapDefaultPackages } from "../Types/Project/DefaultPackageInterface";
import { getAllDefaultPackagesAPI } from "../../Api/Project/DefaultServices/DefaultServicesApi";

interface ProjectDetailsSliceType {
    loading: boolean;
    error: string | null;
    allServices: Service[];
    preCommencementServices: Service[];
    onsiteServices: Service[];
    generalServices: Service[];
    serviceAllContact: ProjectBasicContact[];
    serviceContactWithAssigned: ProjectBasicContact[];
    selectedUserType: string | null;
    serviceContactWithAssignedFiltered: ProjectBasicContact[];
    loadingSubFolderAndDocument: boolean;
    errorSubFolderAndDocument: string | null;
    subFolderAndDocumentList: SubFolderAndDocument[];
    subFolderAllContact: ProjectBasicContact[];
    subFolderAssignedContact: ProjectBasicContact[];
    loadingDefaultPackage: boolean;
    errorDefaultPackage: string | null;
    allDefaultPackages: DefaultPackage[];
    preComencencemetDefaultPackages: DefaultPackage[];
    onsiteDefaultPackages: DefaultPackage[];
    generalDefaultPackages: DefaultPackage[];
}

interface ServiceAndDefaultPackagesType {
    services: Service[];
    defaultPackages: DefaultPackage[]
}

export interface CheckedDefaultPackagesType {
    type: string;
    id: number
}

const initialState: ProjectDetailsSliceType = {
    loading: false,
    error: '',
    allServices: [],
    preCommencementServices: [],
    onsiteServices: [],
    generalServices: [],
    serviceAllContact: [],
    serviceContactWithAssigned: [],
    selectedUserType: null,
    serviceContactWithAssignedFiltered: [],
    loadingSubFolderAndDocument: false,
    errorSubFolderAndDocument: '',
    subFolderAndDocumentList: [],
    subFolderAllContact: [],
    subFolderAssignedContact: [],
    loadingDefaultPackage: false,
    errorDefaultPackage: null,
    allDefaultPackages: [],
    preComencencemetDefaultPackages: [],
    onsiteDefaultPackages: [],
    generalDefaultPackages: [],
}

export const fetchServicesAndDefaulPackages = createAsyncThunk(
    'fetchServicesAndDefaulPackages',
    async (projectId: number) => {
        const token = localStorage.getItem('token');
        const FULL_TOKEN = `Bearer ${token}`;
        const [response1, response2] = await Promise.all([
            fetch(`${baseURL}/api/project-folder/${projectId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': FULL_TOKEN
                },
            }),

            fetch(`${baseURL}/api/get-default-packet`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': FULL_TOKEN
                },
            }),
        ]);

        const servicesRes = await response1.json();

        const defaultPackagesRes = await response2.json();
        const allActiveServices: Service[] = mapCustomServices(servicesRes.data);
        const defaultpackages: DefaultPackage[] = mapDefaultPackages(defaultPackagesRes.data)
        const serviceAndDefaultPackagesType: ServiceAndDefaultPackagesType = {
            services: allActiveServices,
            defaultPackages: defaultpackages
        }

        return serviceAndDefaultPackagesType;
    });

export const fetchAllDefaultPackages = createAsyncThunk(
    'fetchAllDefaultPackages',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getAllDefaultPackagesAPI();

            if (response.status) {
                return mapDefaultPackages(response.data as DefaultPackage[]);
            } else {
                return rejectWithValue(response);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch default services');
        }
    }
);

export const fetchSubFoldersAndDocuments = createAsyncThunk('fetchSubFoldersAndDocuments', async (serviceId: number) => {
    const token = localStorage.getItem('token');
    const FULL_TOKEN = `Bearer ${token}`;

    const [response1, response2] = await Promise.all([
        fetch(`${baseURL}/api/sub-folder/${serviceId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': FULL_TOKEN
            },
        }),
        fetch(`${baseURL}/api/document/${serviceId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': FULL_TOKEN
            },
        })
    ]);

    const subFolderRes = await response1.json();
    const documentsRes = await response2.json();
    const subFolders: SubFolder[] = mapSubFolders(subFolderRes['data'])
    const documents: DocumentInterface[] = mapDocuments(documentsRes['data'])
    const subFolderAndDocumentList: SubFolderAndDocument[] = [];

    for (const subFolder of subFolders) {
        const subFolderAndDocument: SubFolderAndDocument = {
            category: 'sub_folder',
            id: subFolder.sub_folder_id,
            folder_id: subFolder.folder_id,
            title: subFolder.sub_folder_name,
            created_datatime: subFolder.created_datatime,
            //Sub folder
            type: subFolder.type,
            is_default_folder: subFolder.is_default_folder,
            count: subFolder.count,
            assign: subFolder.assign,
            assignedContact: [],
            //Document
            path: '',
        }

        subFolderAndDocumentList.push(subFolderAndDocument);
    }

    for (const document of documents) {
        const subFolderAndDocument: SubFolderAndDocument = {
            category: 'document',
            id: document.document_id,
            folder_id: document.folder_id,
            title: document.title,
            created_datatime: document.created_datatime,
            //Sub folder
            type: '',
            is_default_folder: 0,
            count: 1,
            assign: [],
            assignedContact: [],
            //Document
            path: document.path,
        }

        subFolderAndDocumentList.push(subFolderAndDocument);
    }

    return subFolderAndDocumentList;
}
);

const projectDetailsSlice = createSlice({
    name: 'project details slice',
    initialState: initialState,
    reducers: {
        /*fetchServiceStart: (state) => {
            state.loading = true;
            state.preCommencementServices = [];
            state.onsiteServices = [];
            state.generalServices = [];
            state.error = null;
        },
        fetchServiceSuccess: (state, action: PayloadAction<any[]>) => {
            state.loading = false;
            state.preCommencementServices = mapCustomServices(action.payload.filter(servive => servive.folder === 'Pre Comencencemet'));
            state.onsiteServices = mapCustomServices(action.payload.filter(servive => servive.folder === 'Onsite'));
            state.generalServices = mapCustomServices(action.payload.filter(servive => servive.folder === 'General'));
            
        },
        fetchServiceFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },*/
        setCheckedDefaultPackage: (state, action: PayloadAction<CheckedDefaultPackagesType>) => {
            const contactToUpdate = action.payload.id;

            if (action.payload.type === 'Pre Comencencemet') {
                const existingContact = state.preComencencemetDefaultPackages.find(contact => contact.id === contactToUpdate);

                if (existingContact) {
                    existingContact.icChecked = !existingContact.icChecked;
                }
            } else if (action.payload.type === 'Onsite') {
                const existingContact = state.onsiteDefaultPackages.find(contact => contact.id === contactToUpdate);

                if (existingContact) {
                    existingContact.icChecked = !existingContact.icChecked;
                }
            } else {
                const existingContact = state.generalDefaultPackages.find(contact => contact.id === contactToUpdate);

                if (existingContact) {
                    existingContact.icChecked = !existingContact.icChecked;
                }
            }
        },

        setServiceAllContact: (state, action: PayloadAction<ProjectBasicContact[]>) => {
            if (action.payload && action.payload.length > 0) {
                state.serviceAllContact = action.payload.filter(contact => contact.by !== 'Commercial Client' && contact.by !== 'Domestic Client');
            }
        },
        setAllAssignedContact: (state, action: PayloadAction<ShareContact[]>) => {
            state.serviceContactWithAssigned = [];
            const assignedContacts: ShareContact[] = action.payload;
            const assignedContactSet = new Set(assignedContacts.map(contact => contact.id));

            state.serviceContactWithAssigned = state.serviceAllContact;

            /*
            state.serviceContactWithAssigned = state.serviceAllContact.map(contact => ({
                ...contact,
                icChecked: assignedContactSet.has(contact.id)
            }));
            */
        },

        /*setSelectedUserType: (state, action : PayloadAction<string | null>) => {
            state.selectedUserType = action.payload;

            
            if(state.selectedUserType){
                const projectContacts = state.serviceContactWithAssigned;
                state.serviceContactWithAssignedFiltered = projectContacts.filter((contact) => (contact.contactType === state.selectedUserType))

            } else if (state.selectedUserRole){
                const projectContacts = state.serviceContactWithAssigned;
                state.serviceContactWithAssignedFiltered = projectContacts.filter((contact) => (contact.role_name === state.selectedUserRole?.role_name) )

            }else if (state.selectedUserType){
                const projectContacts = state.serviceContactWithAssigned;
                state.serviceContactWithAssignedFiltered = projectContacts.filter((contact) => (contact.contactType === state.selectedUserType) )

            } else{
                state.serviceContactWithAssignedFiltered = state.serviceContactWithAssigned;

            }

        },*/

        setCheckedContactForAssign: (state, action: PayloadAction<ProjectBasicContact>) => {
            const contactToUpdate = action.payload;
            const existingContact = state.serviceContactWithAssigned.find(contact => contact.id === contactToUpdate.id);

            if (existingContact) {
                existingContact.icChecked = !existingContact.icChecked;
            }
        },

        setSubFolderAllContact: (state, action: PayloadAction<ShareContact[]>) => {
            const allAssignedContact: ProjectBasicContact[] = [];
            for (const shareCon of action.payload) {
                const foundContact = state.serviceAllContact.find(con => con.id === shareCon.id)

                if (foundContact) {
                    allAssignedContact.push(foundContact);
                }
            }

            state.subFolderAllContact = allAssignedContact;
        },

        setSubFolderAssignedContact: (state, action: PayloadAction<ShareContact[]>) => {
            state.subFolderAssignedContact = [];
            const assignedContacts: ShareContact[] = action.payload;
            const assignedContactSet = new Set(assignedContacts.map(contact => contact.id));

            state.subFolderAssignedContact = state.subFolderAllContact.map(contact => ({
                ...contact,
                icChecked: assignedContactSet.has(contact.id)
            }));
        },

        setCheckedSubFolderContactForAssign: (state, action: PayloadAction<ProjectBasicContact>) => {
            const contactToUpdate = action.payload;
            const existingContact = state.subFolderAssignedContact.find(contact => contact.id === contactToUpdate.id);

            if (existingContact) {
                existingContact.icChecked = !existingContact.icChecked;
            }
        },

        fetchDefaultPackagesStart: (state) => {
            state.loadingDefaultPackage = true;
            state.allDefaultPackages = [];
            state.preComencencemetDefaultPackages = [];
            state.onsiteDefaultPackages = [];
            state.generalDefaultPackages = [];
            state.errorDefaultPackage = null;
        },

        fetchDefaultPackagesSuccess: (state, action: PayloadAction<any[]>) => {
            state.loadingDefaultPackage = false;
            const defPcks: DefaultPackage[] = mapDefaultPackages(action.payload);

            state.allDefaultPackages = defPcks
            state.preComencencemetDefaultPackages = defPcks.filter((pck) => pck.tab === 'Pre Comencencemet')
            state.onsiteDefaultPackages = defPcks.filter((pck) => pck.tab === 'Onsite')
            state.generalDefaultPackages = defPcks.filter((pck) => pck.tab === 'General')
        },

        fetchDefaultPackagesFailure: (state, action: PayloadAction<string>) => {
            state.loadingDefaultPackage = false;
            state.errorDefaultPackage = action.payload;
        },

    }, extraReducers(builder) {
        builder
            .addCase(fetchSubFoldersAndDocuments.pending, (state) => {
                state.loadingSubFolderAndDocument = true;
                state.errorSubFolderAndDocument = null;
            })
            .addCase(fetchSubFoldersAndDocuments.fulfilled, (state, action: PayloadAction<SubFolderAndDocument[]>) => {
                state.loadingSubFolderAndDocument = false;
                const subFolderAndDocument = action.payload;
                const subFolderAssignedAllContact = JSON.parse(JSON.stringify(state.subFolderAllContact));
                const subFolderAndDocumentFinal: SubFolderAndDocument[] = [];

                for (const subAndDoc of subFolderAndDocument) {
                    const allAssignedContact: ProjectBasicContact[] = [];

                    for (const shareCon of subAndDoc.assign) {
                        const foundContact = subFolderAssignedAllContact.find((con: ProjectBasicContact) => con['id'] === shareCon.id)
                        if (foundContact) {
                            allAssignedContact.push(foundContact);
                        }
                    }

                    subAndDoc.assignedContact = allAssignedContact
                    subFolderAndDocumentFinal.push(subAndDoc)
                }

                state.subFolderAndDocumentList = subFolderAndDocumentFinal;
            })
            .addCase(fetchSubFoldersAndDocuments.rejected, (state, action) => {
                state.loadingSubFolderAndDocument = false;
                state.errorSubFolderAndDocument = action.error.message || 'Failed to fetch data';
            })
            .addCase(fetchServicesAndDefaulPackages.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchServicesAndDefaulPackages.fulfilled, (state, action: PayloadAction<ServiceAndDefaultPackagesType>) => {
                state.loading = false;
                state.allServices = action.payload.services;
                state.preCommencementServices = action.payload.services.filter(servive => servive.folder === 'Pre Comencencemet');
                state.onsiteServices = action.payload.services.filter(servive => servive.folder === 'Onsite');
                state.generalServices = action.payload.services.filter(servive => servive.folder === 'General');

                const ids = action.payload.services.map((service) => service.add_default_id).filter((id): id is number => id !== null);
                const assignedIds = new Set(ids.map(contact => contact));

                const defaultPaks: DefaultPackage[] = action.payload.defaultPackages.map(contact => ({
                    ...contact,
                    icChecked: assignedIds.has(contact.id)
                }));

                state.allDefaultPackages = defaultPaks
                state.preComencencemetDefaultPackages = defaultPaks.filter((pck) => pck.tab === 'Pre Comencencemet')
                state.onsiteDefaultPackages = defaultPaks.filter((pck) => pck.tab === 'Onsite')
                state.generalDefaultPackages = defaultPaks.filter((pck) => pck.tab === 'General')
            })
            .addCase(fetchServicesAndDefaulPackages.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch data';
            })

            /* Fetch all default packages */
            .addCase(fetchAllDefaultPackages.pending, state => {
                state.loadingDefaultPackage = true;
                state.errorDefaultPackage = null;
            })
            .addCase(fetchAllDefaultPackages.fulfilled, (state, action: PayloadAction<DefaultPackage[]>) => {
                state.loadingDefaultPackage = false;
                state.allDefaultPackages = action.payload;
            })
            .addCase(fetchAllDefaultPackages.rejected, (state, action) => {
                state.loadingDefaultPackage = false;
                state.errorDefaultPackage = action.error.message ?? "Failed to fetch default services.";
            });
    },
});

export const {
    setServiceAllContact,
    setAllAssignedContact,
    setCheckedContactForAssign,
    setSubFolderAllContact,
    setSubFolderAssignedContact,
    setCheckedSubFolderContactForAssign,
    fetchDefaultPackagesStart,
    fetchDefaultPackagesSuccess,
    fetchDefaultPackagesFailure,
    setCheckedDefaultPackage
} = projectDetailsSlice.actions
export default projectDetailsSlice.reducer